import useScrollReveal from '@/hooks/useScrollReveal';
import ImagePlaceholder from '@/components/base/ImagePlaceholder';

interface CinematicDividerProps {
  quote: string;
  subquote?: string;
  tall?: boolean;
  image?: string;
  imageAlt?: string;
  imageTitle?: string;
}

export default function CinematicDivider({
  quote,
  subquote,
  tall = false,
  image,
  imageAlt = '',
  imageTitle,
}: CinematicDividerProps) {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.2 });

  return (
    <section
      ref={ref}
      className={`relative overflow-hidden ${tall ? 'h-[55vh] min-h-[380px]' : 'h-[42vh] min-h-[300px]'}`}
    >
      <div className="absolute inset-0 w-full h-full">
        {image ? (
          <>
            <img
              src={image}
              alt={imageAlt}
              title={imageTitle || imageAlt}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60"></div>
          </>
        ) : (
          <ImagePlaceholder className="w-full h-full border-0" labelPosition="top" />
        )}
      </div>

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-5">
        <div
          className={`flex items-center justify-center gap-3 mb-5 md:mb-7 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          aria-hidden="true"
        >
          <span className="h-px w-12 md:w-16 bg-foreground-50/40"></span>
          <span className="w-1.5 h-1.5 rotate-45 bg-accent-500"></span>
          <span className="h-px w-12 md:w-16 bg-foreground-50/40"></span>
        </div>
        <h2
          className={`font-heading text-3xl sm:text-4xl md:text-5xl text-foreground-50 font-light leading-tight max-w-4xl transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {quote}
        </h2>
        {subquote && (
          <p
            className={`mt-4 md:mt-6 font-heading text-lg md:text-2xl italic text-foreground-50/70 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {subquote}
          </p>
        )}
      </div>
    </section>
  );
}